import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';
import { DatePicker } from '@/components/atoms/DatePicker';
import { RichTextEditor } from '@/components/organisms/RichTextEditor';
import { toast } from '@/store/toastStore';

/**
 * 어드민 약관 작성/수정 페이지 (모달 → 페이지 전환).
 * 라우트:
 *  - /admin/terms/new
 *  - /admin/terms/:id/edit
 */

interface Term {
  id: number;
  type: string;
  title: string;
  content: string;
  effective_date: string | null;
  is_active: boolean;
}

const TERM_TYPES: Array<{ key: string; label: string }> = [
  { key: 'service', label: '서비스 이용약관' },
  { key: 'privacy', label: '개인정보처리방침' },
  { key: 'disclaimer', label: '투자 위험 고지' },
  { key: 'operation', label: '운영정책' },
];

export function TermsFormPage() {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const editId = params.id ? Number(params.id) : null;
  const isEdit = editId !== null && !Number.isNaN(editId);
  const qc = useQueryClient();

  const [type, setType] = useState<string>('service');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  /* HTML 편집 모드 — Rich 에디터로 처리 안 되는 구조(예: 마지막 단독 외부 링크의 버튼 스타일 회피)
     를 운영자가 직접 손보기 위한 raw HTML 편집 토글 */
  const [htmlMode, setHtmlMode] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  const editQ = useQuery({
    queryKey: ['admin', 'terms', editId ?? -1],
    queryFn: () => get<{ term: Term }>(`/admin/terms/${editId}`),
    enabled: isEdit,
  });
  useEffect(() => {
    if (!isEdit || hydrated) return;
    const t = editQ.data?.term;
    if (!t) return;
    setType(t.type);
    setTitle(t.title);
    setContent(t.content);
    setEffectiveDate((t.effective_date ?? '').slice(0, 10));
    setIsActive(!!t.is_active);
    setHydrated(true);
  }, [isEdit, editQ.data, hydrated]);

  const submitMut = useMutation({
    mutationFn: async () => {
      const body = {
        type,
        title,
        content,
        effective_date: effectiveDate || null,
        is_active: isActive,
      };
      if (isEdit && editId) {
        await put(`/admin/terms/${editId}`, body);
        return { id: editId };
      }
      return post<{ id: number }>('/admin/terms', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'terms'] });
      qc.invalidateQueries({ queryKey: ['terms'] });
      toast.success(isEdit ? '수정 완료' : '등록 완료');
      navigate('/admin/terms', { replace: true });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? '저장 실패');
    },
  });

  if (isEdit && editQ.isLoading) {
    return (
      <div className="w-full">
        <Skeleton className="mb-4 h-9 w-1/3" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  const canSubmit = title.trim().length >= 1 && content.trim().length >= 1;

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">
          {isEdit ? '약관 수정' : '새 약관 등록'}
        </h1>
        <Button variant="ghost" onClick={() => navigate('/admin/terms')}>
          목록으로
        </Button>
      </div>

      <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div>
          <label className="mb-1 block text-sm font-medium">유형</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-10 w-full max-w-sm rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm"
          >
            {TERM_TYPES.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">
            제목 <span className="text-[var(--color-fall)]">*</span>
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="약관 제목"
            maxLength={200}
          />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="block text-sm font-medium">
              본문 <span className="text-[var(--color-fall)]">*</span>
            </label>
            <button
              type="button"
              onClick={() => setHtmlMode((v) => !v)}
              className={
                'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ' +
                (htmlMode
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-sub)] hover:border-[var(--color-primary)]/40')
              }
              title="raw HTML 직접 편집 (앵커 태그 위치 등 미세 조정용)"
            >
              {htmlMode ? '에디터 모드로' : '< > HTML 편집'}
            </button>
          </div>
          {htmlMode ? (
            <>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="<p>...</p>"
                rows={20}
                spellCheck={false}
                className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 font-mono text-xs leading-relaxed text-[var(--color-text)] focus:border-[var(--color-primary)] focus:outline-none"
              />
              <p className="mt-1.5 text-[11px] leading-relaxed text-[var(--color-text-sub)]">
                ⚠️ 저장 시 서버가 새니타이즈 처리합니다(허용 외 태그/속성 제거). a 태그는 href·target·rel 만 유지돼요.
                <br />
                💡 마지막 &lt;p&gt;의 외부 링크가 단독이면 사용자 페이지에서 파란 버튼으로 표시됩니다.
                다른 텍스트와 함께 묶거나 별도 줄로 두면 일반 링크가 돼요.
              </p>
            </>
          ) : (
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="약관 본문을 입력하세요 (제목은 H2/H3, 본문은 일반 단락)"
            />
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium">시행일 (선택)</label>
          <DatePicker
            value={effectiveDate}
            onChange={setEffectiveDate}
            placeholder="시행일 선택"
            ariaLabel="약관 시행일"
            className="max-w-sm"
          />
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded accent-[var(--color-primary)]"
          />
          활성화 (사용자 페이지에 노출)
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => navigate('/admin/terms')}>
            취소
          </Button>
          <Button
            disabled={!canSubmit}
            loading={submitMut.isPending}
            onClick={() => submitMut.mutate()}
          >
            {isEdit ? '수정 완료' : '등록'}
          </Button>
        </div>
      </div>
    </div>
  );
}
